import { MapPinIcon } from '@heroicons/react/24/outline';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { Chip, LinkButton, MetricTile, PageHeader, StateBlock } from '../components/ui';
import { HeatmapCard, HoursCard, TrendCard, WeekdayCard } from '../features/home/Charts';
import { dayFormatter } from '../features/home/format';
import { LiveOrders } from '../features/home/LiveOrders';
import { OrderTypeCard, PaymentCard, TimingCard, TopItemsCard } from '../features/home/Mix';
import { RangePicker } from '../features/home/RangePicker';
import { dayCount, readRange, rangeQuery, todayIn } from '../features/home/range';
import { SetupChecklist } from '../features/home/SetupChecklist';
import { Tiles } from '../features/home/Tiles';
import { useHomeData, useVenueZone } from '../features/home/useHomeData';
import { useActiveLocation } from '../hooks/useActiveLocation';
import { usePageTitle } from '../hooks/usePageTitle';
import type { RootState } from '../store';
import { usePendingCount } from '../hooks/usePendingOrders';

const Dashboard = () => {
    const { t } = useTranslation('home');
    const { t: tc, i18n } = useTranslation();
    usePageTitle(t('pageTitle'));

    const role = useSelector((s: RootState) => s.auth.user?.role?.name);
    const overview = role !== 'Staff';
    const isOwner = role === 'Owner' || role === 'Admin';

    const [params, setParams] = useSearchParams();
    const { zone } = useVenueZone();
    const today = zone ? todayIn(zone) : null;
    // The range lives in the URL: ?range=7d or ?from=&to=. Days are the venue's, not the browser's.
    const range = useMemo(() => (today ? readRange(params, today) : null), [params, today]);
    const { locations: options, locationId } = useActiveLocation();
    const { count } = usePendingCount();
    const data = useHomeData(range, { overview, setup: isOwner });

    // Weekday patterns only make sense over at least a week.
    const weekly = range !== null && dayCount(range.from, range.to) >= 7;
    const choose = (query: ReturnType<typeof rangeQuery>) => setParams(query, { replace: true });

    const name = locationId === null ? tc('shell.allLocations') : (options.find((o) => o.id === locationId)?.name ?? '');
    const noLocation = data.locations.state === 'ready' && data.locations.data?.length === 0;

    const day = dayFormatter(i18n.language, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    const shortDay = dayFormatter(i18n.language, { day: 'numeric', month: 'long', year: 'numeric' });
    const span = range && (range.from === range.to ? day(range.from) : `${shortDay(range.from)} \u2013 ${shortDay(range.to)}`);
    const meta = (
        <>
            {span && t('meta', { range: span, zone })}
            {data.current && (
                <>
                    {' · '}
                    <Chip variant={data.current.acceptingOrders ? 'success' : 'neutral'}>
                        {data.current.acceptingOrders ? t('accepting') : t('paused')}
                    </Chip>
                </>
            )}
        </>
    );

    return (
        <>
            <PageHeader
                title={t('title', { location: name })}
                meta={meta}
                actions={
                    <>
                        {overview && !noLocation && range && today && (
                            <RangePicker
                                value={range}
                                today={today}
                                onPreset={(p) => choose(rangeQuery(p))}
                                onCustom={(from, to) => choose(rangeQuery({ from, to }))}
                            />
                        )}
                        <LinkButton to="/orders" variant="strong">
                            {t('openBoard')}
                        </LinkButton>
                    </>
                }
            />

            {noLocation ? (
                <div className="grid gap-4">
                    <StateBlock
                        variant="empty"
                        icon={MapPinIcon}
                        title={t('empty.title')}
                        body={t('empty.body')}
                        action={
                            isOwner && (
                                <LinkButton to="/locations/create" variant="primary">
                                    {t('empty.action')}
                                </LinkButton>
                            )
                        }
                    />
                    {data.setup && data.setup.some((s) => !s.done) && <SetupChecklist steps={data.setup} locations={[]} />}
                </div>
            ) : (
                <div className="grid gap-4">
                    <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-5">
                        <div className="col-span-2 lg:col-span-1">
                            <MetricTile
                                label={t('tiles.needsAction')}
                                value={count}
                                delta={count > 0 ? t('tiles.needsActionOne') : t('tiles.allCaughtUp')}
                                to="/orders"
                                attention={count > 0}
                            />
                        </div>
                        {overview && <Tiles remote={data.stats} />}
                    </div>

                    <div className={overview ? 'grid gap-4 lg:grid-cols-[3fr_2fr]' : 'grid gap-4'}>
                        <LiveOrders remote={data.live} />
                        {overview && <TopItemsCard remote={data.stats} />}
                    </div>

                    {overview && (
                        <>
                            <TrendCard remote={data.stats} />
                            <div className="grid gap-4 md:grid-cols-2">
                                <HoursCard remote={data.stats} className={weekly ? undefined : 'md:col-span-2'} />
                                {weekly && <WeekdayCard remote={data.stats} />}
                            </div>
                            {weekly && <HeatmapCard remote={data.stats} />}
                            <div className="grid gap-4 md:grid-cols-2">
                                <OrderTypeCard remote={data.stats} />
                                <PaymentCard remote={data.stats} />
                            </div>
                            <TimingCard remote={data.stats} />
                        </>
                    )}

                    {data.setup && data.setup.some((s) => !s.done) && (
                        <SetupChecklist steps={data.setup} locations={data.locations.data ?? []} />
                    )}
                </div>
            )}
        </>
    );
};

export default Dashboard;
